import { useState, useEffect, useCallback } from 'react'

const ONBOARDING_COMPLETED_KEY = 'onboarding_completed'
const ONBOARDING_SKIPPED_STEPS_KEY = 'onboarding_skipped_steps'

// Step name mapping for ProfileCompletionWizard
export const ONBOARDING_STEP_NAMES = {
  STEP_1: 'business_details',
  STEP_2: 'profile_photo',
  STEP_3: 'bio_location',
  STEP_4: 'gallery',
  STEP_5: 'services',
  STEP_6: 'availability',
} as const

export type OnboardingStepName = typeof ONBOARDING_STEP_NAMES[keyof typeof ONBOARDING_STEP_NAMES]

interface UseOnboardingReturn {
  isOnboarding: boolean
  hasCompletedOnboarding: boolean
  completeOnboarding: () => void
  startOnboarding: () => void
  shouldShowOnboarding: (completeness: number, profile: unknown, isFirstLogin?: boolean) => boolean
  getSkippedSteps: () => OnboardingStepName[]
  saveSkippedStep: (stepName: OnboardingStepName) => void
  clearSkippedSteps: () => void
}

export function useOnboarding(): UseOnboardingReturn {
  const [isOnboarding, setIsOnboarding] = useState(false)
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false)

  useEffect(() => {
    const completed = localStorage.getItem(ONBOARDING_COMPLETED_KEY)
    setHasCompletedOnboarding(completed === 'true')
  }, [])

  const completeOnboarding = useCallback(() => {
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true')
    setHasCompletedOnboarding(true)
    setIsOnboarding(false)
  }, [])

  const startOnboarding = useCallback(() => {
    setIsOnboarding(true)
  }, [])

  const shouldShowOnboarding = useCallback((completeness: number, profile: unknown, isFirstLogin = false) => {
    // Show onboarding if not completed AND (completeness === 0 OR first login)
    // OR if user previously started but didn't finish
    const hasProfile = profile && typeof profile === 'object' && Object.keys(profile).length > 0
    const isNewUser = completeness === 0 || isFirstLogin || !hasProfile
    const skippedSteps = getSkippedSteps()
    const hasUnfinishedSteps = skippedSteps.length > 0
    return !hasCompletedOnboarding && (isNewUser || hasUnfinishedSteps)
  }, [hasCompletedOnboarding])

  const getSkippedSteps = useCallback((): OnboardingStepName[] => {
    const stored = localStorage.getItem(ONBOARDING_SKIPPED_STEPS_KEY)
    if (!stored) return []
    try {
      return JSON.parse(stored) as OnboardingStepName[]
    } catch {
      return []
    }
  }, [])

  const saveSkippedStep = useCallback((stepName: OnboardingStepName) => {
    const currentSteps = getSkippedSteps()
    if (!currentSteps.includes(stepName)) {
      const updatedSteps = [...currentSteps, stepName]
      localStorage.setItem(ONBOARDING_SKIPPED_STEPS_KEY, JSON.stringify(updatedSteps))
    }
  }, [getSkippedSteps])

  const clearSkippedSteps = useCallback(() => {
    localStorage.removeItem(ONBOARDING_SKIPPED_STEPS_KEY)
  }, [])

  return {
    isOnboarding,
    hasCompletedOnboarding,
    completeOnboarding,
    startOnboarding,
    shouldShowOnboarding,
    getSkippedSteps,
    saveSkippedStep,
    clearSkippedSteps,
  }
}
